/* Shared building blocks for the Supply › Reports screens:
 *  - ReportHub: the card grid landing page (one card per sub-report)
 *  - RackCell: a lot's storage rack, distinguishing a recorded one from a guess
 *  - useWarehouseLots: fetch all on-hand lots for the scoped warehouse */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import supplyService from "../../../Services/supplyService";
import { useWarehouse } from "../../Layout/WarehouseContext";
import { SectionHeader, Td, IconChevRight } from "../supplyKit";

/* ---------- Report hub (card grid) ---------- */
export const ReportHub = ({ title, sub, cards }) => {
  const navigate = useNavigate();
  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <SectionHeader title={title} sub={sub} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(300px, 100%), 1fr))", gap: 16 }}>
        {cards.map((c) => (
          <button
            key={c.path}
            type="button"
            onClick={() => navigate(c.path)}
            className="cb-report-card"
            style={{
              display: "flex",
              alignItems: "stretch",
              textAlign: "left",
              padding: 0,
              border: "1px solid var(--border-default)",
              borderRadius: 10,
              background: "var(--bg-panel)",
              cursor: "pointer",
              overflow: "hidden",
              minHeight: 116,
              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            }}
          >
            <div style={{ flex: 1, padding: 18 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "var(--fg-strong)", marginBottom: 6 }}>
                {c.title}
              </div>
              <div style={{ fontSize: 13, color: "var(--fg-muted)", lineHeight: 1.45 }}>{c.desc}</div>
            </div>
            <div
              style={{
                width: 56,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "var(--brand)",
                color: "var(--brand-fg)",
              }}
            >
              <ArrowRight size={20} strokeWidth={2} />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

/* ---------- Back link for sub-report detail screens ---------- */
export const ReportBack = ({ to, label = "Reports" }) => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(to)}
      style={{
        alignSelf: "flex-start",
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        border: 0,
        background: "transparent",
        color: "var(--fg-link)",
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
        padding: 0,
      }}
    >
      <IconChevRight size={13} style={{ transform: "rotate(180deg)" }} />
      Back to {label}
    </button>
  );
};

/* ---------- A lot's rack, read-only ----------
   Reports state where stock is, so they must not state a guess as a fact. A rack
   with rackSource HOME was never recorded against this batch — the backend
   answered with where the medicine belongs — so it renders muted and italic, the
   same way Inventory shows it. PLACED renders plainly. Editing a batch's location
   belongs on Inventory, not in a report. */
export const RackCell = ({ lot }) => {
  const placed = lot.rackSource === "PLACED";
  if (!lot.rackCode) return <Td mono>—</Td>;
  return (
    <Td
      mono
      style={placed ? undefined : { color: "var(--fg-muted)", fontStyle: "italic" }}
      title={
        placed
          ? `Recorded in rack ${lot.rackCode}`
          : `Not put away — ${lot.medicineName || lot.medId} belongs on ${lot.rackCode}. Set the batch's actual rack on the Inventory screen.`
      }
    >
      {lot.rackCode}
    </Td>
  );
};

/* ---------- Load every on-hand lot for the store in scope ----------
   Used by reports that derive client-side (expiry, low stock) and so need
   the full set, not a single page.

   The warehouse comes from the app-wide selection (the topbar switcher), not from
   the report — switching store there refetches every report that uses this. */
export const useWarehouseLots = () => {
  const { code: wh, isAll } = useWarehouse();
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!wh) {
      setLots([]);
      setLoading(false);
      /* Stock lives at one warehouse and the endpoint takes it in the path
         (v1/inventory/warehouse/{id}), so there is no all-stores stock query to
         make. Say that instead of rendering an empty table that reads as "no
         stock" — which is a very different and alarming statement. */
      setError(
        isAll
          ? "Stock is held per store, so this report needs a single one. Pick a store in the topbar."
          : null
      );
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    supplyService
      .getInventoryLots(wh, { page: 0, size: 2000 })
      .then((res) => {
        if (!alive) return;
        setLots(res.content || []);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load stock.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [wh, isAll, reloadKey]);

  /* No setWh / warehouses: the store is chosen once, app-wide. `wh` is still
     returned because reports name it in exports and empty states. */
  return { wh, isAll, lots, loading, error, reload: () => setReloadKey((k) => k + 1) };
};
